// React-Query hooks for /api/me/signatures (multi-signature per account).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export interface SignatureRecord {
  id: number;
  accountId: number;
  name: string;
  isDefault: boolean;
  html: string;
  updatedAt: string;
}

export function useSignatures() {
  return useQuery<{ signatures: SignatureRecord[] }>({
    queryKey: ['signatures'],
    queryFn: async () => {
      const r = await fetch('/api/me/signatures', { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
    staleTime: 30_000,
  });
}

export function useCreateSignature() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { accountId: number; name: string; html: string }) => {
      const r = await fetch('/api/me/signatures', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${r.status}`);
      }
      return r.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['signatures'] }),
  });
}

export function useUpdateSignature() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: number;
      name?: string;
      html?: string;
      isDefault?: boolean;
    }) => {
      const { id, ...patch } = input;
      const r = await fetch(`/api/me/signatures/${id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${r.status}`);
      }
      return r.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['signatures'] }),
  });
}

export function useDeleteSignature() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const r = await fetch(`/api/me/signatures/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['signatures'] }),
  });
}

/** Signatures for one account, default first. */
export function signaturesForAccount(
  all: SignatureRecord[] | undefined,
  accountId: number | null,
): SignatureRecord[] {
  if (accountId == null || !all) return [];
  return all
    .filter((s) => s.accountId === accountId)
    .sort((a, b) => Number(b.isDefault) - Number(a.isDefault) || a.id - b.id);
}
